import { useEffect, useMemo, useState } from 'react'
import Highcharts from 'highcharts/highmaps'
import HighchartsReact from 'highcharts-react-official'
import tiledwebmap from 'highcharts/modules/tiledwebmap'

if (typeof tiledwebmap === 'function') {
  tiledwebmap(Highcharts)
}

export default function GeographicSalesIntelligenceHeatmap({
  heatmap,
  venues,
}) {
  const cells = useMemo(
    () =>
      (Array.isArray(heatmap) ? heatmap : []).filter(
        (cell) => cell.lat != null && cell.lon != null
      ),
    [heatmap]
  )
  const venuePoints = useMemo(
    () =>
      (Array.isArray(venues) ? venues : []).filter(
        (venue) => venue.latitude != null && venue.longitude != null
      ),
    [venues]
  )
  const [mapReady, setMapReady] = useState(false)

  useEffect(() => {
    setMapReady(true)
  }, [])

  const primaryVenue = venuePoints[0]
  const options = useMemo(() => {
    const maxBuyers = Math.max(
      1,
      ...cells.map((cell) => Number(cell.buyers) || 0)
    )
    return {
      chart: {
        height: 420,
        backgroundColor: 'transparent',
        margin: [8, 8, 8, 8],
      },
      title: { text: undefined },
      credits: { enabled: true },
      legend: { enabled: true },
      mapNavigation: {
        enabled: true,
        buttonOptions: { alignTo: 'spacingBox' },
      },
      mapView: {
        projection: { name: 'WebMercator' },
        ...(primaryVenue
          ? {
              center: [primaryVenue.longitude, primaryVenue.latitude],
              zoom: cells.length > 0 ? 6 : 7,
            }
          : {}),
      },
      colorAxis: {
        min: 0,
        max: maxBuyers,
        stops: [
          [0, '#fb923c'],
          [0.05, '#f97316'],
          [0.18, '#ea580c'],
          [0.45, '#c2410c'],
          [1, '#7c2d12'],
        ],
      },
      tooltip: {
        formatter() {
          if (this.series.name === 'Venue') {
            return `<b>${this.point.name}</b><br/>Venue`
          }
          return `<b>${this.point.z} buyers</b><br/>${this.point.tickets} tickets`
        },
      },
      series: [
        {
          type: 'tiledwebmap',
          name: 'Basemap',
          provider: { type: 'OpenStreetMap' },
          showInLegend: false,
          enableMouseTracking: false,
        },
        {
          type: 'mappoint',
          name: 'Buyers',
          colorKey: 'z',
          data: cells.map((cell) => {
            const buyers = Number(cell.buyers) || 0
            return {
              lat: cell.lat,
              lon: cell.lon,
              z: buyers,
              tickets: cell.tickets,
              name: `${buyers} buyers`,
              marker: {
                radius: buyerMarkerRadius(buyers),
              },
            }
          }),
          dataLabels: {
            enabled: true,
            y: -14,
            style: {
              color: '#111827',
              fontSize: '11px',
              fontWeight: '600',
              textOutline: '2px #ffffff',
            },
          },
          marker: {
            lineWidth: 2,
            lineColor: '#ffffff',
            radius: 11,
            fillOpacity: 0.95,
          },
        },
        {
          type: 'mappoint',
          name: 'Venue',
          color: '#111827',
          data: venuePoints.map((venue) => ({
            lat: venue.latitude,
            lon: venue.longitude,
            name: venue.name || 'Venue',
            z: 0,
            tickets: 0,
          })),
          colorAxis: false,
          marker: {
            symbol: 'mapmarker',
            radius: 12,
            fillColor: '#111827',
            lineColor: '#ffffff',
            lineWidth: 2,
          },
        },
      ],
    }
  }, [cells, primaryVenue, venuePoints])

  if (!mapReady) {
    return <p className="text-muted small mb-0">Loading map…</p>
  }

  if (!cells.length && !venuePoints.length) {
    return (
      <p className="text-muted small mb-0">
        No buyer coordinates yet. Complete website orders with IP geolocation
        will appear here.
      </p>
    )
  }

  return (
    <HighchartsReact
      highcharts={Highcharts}
      constructorType="mapChart"
      options={options}
    />
  )
}

function buyerMarkerRadius(buyers) {
  return Math.round(10 + Math.min(6, Math.sqrt(Math.max(buyers, 0))))
}
